
import React, { useEffect, useContext, useRef } from "react";
import { AppContext } from "src/App";
import { getOrganisationDetailsbyCountry } from "src/network/ApiService";

const STAGE_ENV_PREFIXES = new Set(['stg', 'perf', 'qa', 'gateway.pearson.com']);
const PROD_HOSTNAMES = new Set(['homepage.pearson.com', 'ggw.pearson.com']);
const EMBEDDED_MESSAGING_SCRIPT_ID = 'ah-embedded-messaging-bootstrap';

const STAGE_CHAT_CONFIG = {
    domainUrl: 'https://pearson--testbau.sandbox.my.site.com',
    scriptUrl: 'https://pearson--testbau.sandbox.my.salesforce-scrt.com',
    initId: '00D9O00000OkX4n',
    authenticated: {
        typeId: 'A_Q_ActiveHub_Authenticated',
        authenticId: 'ESWAQActiveHubAuthenti1784109460089'
    },
    unauthenticated: {
        typeId: 'A_Q_ActiveHub_UnAuthenticated',
        authenticId: 'ESWAQActiveHubUnAuthen1784109872175'
    }
};

const PROD_CHAT_CONFIG = {
    domainUrl: 'https://pearson.my.site.com',
    scriptUrl: 'https://pearson.my.salesforce-scrt.com',
    initId: '00Db0000000e0e2',
    authenticated: {
        typeId: 'AQ_ActiveHub_Authenticated',
        authenticId: 'ESWAQActiveHubAuthentic1786620626621'
    },
    unauthenticated: {
        typeId: 'AQ_ActiveHub_UnAuthenticated',
        authenticId: 'ESWAQActiveHubUnAuthent1786620841436'
    }
};

const decodeUserField = (entities, value) => {
    if (!value) {
        return '';
    }

    if (typeof entities?.decode !== 'function') {
        return value;
    }

    try {
        return entities.decode(entities.decode(value));
    } catch (error) {
        return value;
    }
};

const LiveChatButton = ({ isAuthenticated, identityToken }) => {
    const appContext = useContext(AppContext) || {};
    const { entities } = appContext;
    const selectedCountryRef = useRef('');
    const countrySubscriptionRef = useRef(null);

    const getOrganizationId = (activeHubUser = {}) => {
        return (
            activeHubUser?.organizationId ||
            activeHubUser?.orgId ||
            window.GATEWAY?.orgId ||
            ''
        );
    };

    const getCountryNameFromResponse = (response, fallback = '') => {
        return (
            response?.countryName ||
            response?.data?.countryName ||
              fallback ||
            ''
        );
    };

    const applyAuthenticatedChatContext = () => {
        if (!isAuthenticated || !window.embeddedservice_bootstrap) {
            return;
        }

        const activeHubUser = window.USERPROFILE || {};
        const firstName = decodeUserField(entities, activeHubUser?.firstName);
        const lastName = decodeUserField(entities, activeHubUser?.lastName);
        const email = activeHubUser?.email || activeHubUser?.userName || '';
        const selectedCountry = selectedCountryRef.current || 'United Kingdom';

        try {
            if (identityToken && window.embeddedservice_bootstrap.userVerificationAPI?.setIdentityToken) {
                window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
                    identityTokenType: "JWT",
                    identityToken: identityToken
                });
            }

            if (window.embeddedservice_bootstrap.prechatAPI?.setHiddenPrechatFields) {
                window.embeddedservice_bootstrap.prechatAPI.setHiddenPrechatFields({
                    firstName,
                    lastName,
                    email,
                    selectedCountry
                });
            }
        } catch (error) {
            console.error('Error applying Embedded Messaging context: ', error);
        }
    };

    useEffect(() => {
        if (!isAuthenticated) {
            return undefined;
        }

        const activeHubUser = window.USERPROFILE || {};
        const fallbackCountry = activeHubUser?.selectedCountry || '';
        const organizationId = getOrganizationId(activeHubUser);
        selectedCountryRef.current = fallbackCountry;

        if (!organizationId) {
            applyAuthenticatedChatContext();
            return undefined;
        }

        countrySubscriptionRef.current?.unsubscribe?.();
        countrySubscriptionRef.current = getOrganisationDetailsbyCountry(organizationId).subscribe(
            (response) => {
                selectedCountryRef.current = getCountryNameFromResponse(response, fallbackCountry);
                applyAuthenticatedChatContext();
            },
            () => {
                selectedCountryRef.current = fallbackCountry;
                applyAuthenticatedChatContext();
            }
        );

        return () => {
            countrySubscriptionRef.current?.unsubscribe?.();
        };
    }, [isAuthenticated, identityToken]);

    useEffect(() => {
        const hostname = window.location.hostname?.toLowerCase() || "";
        const hostnamePrefix = hostname.split("-")[0];

        let chatConfig = null;

        if (STAGE_ENV_PREFIXES.has(hostnamePrefix)) {
            const authConfig = isAuthenticated
                ? STAGE_CHAT_CONFIG.authenticated
                : STAGE_CHAT_CONFIG.unauthenticated;

            chatConfig = {
                domainUrl: STAGE_CHAT_CONFIG.domainUrl,
                scriptUrl: STAGE_CHAT_CONFIG.scriptUrl,
                initId: STAGE_CHAT_CONFIG.initId,
                typeId: authConfig.typeId,
                authenticId: authConfig.authenticId
            };
        } else if (PROD_HOSTNAMES.has(hostname)) {
                 const authConfig = isAuthenticated
                ? PROD_CHAT_CONFIG.authenticated
                : PROD_CHAT_CONFIG.unauthenticated;

            chatConfig = {
                domainUrl: PROD_CHAT_CONFIG.domainUrl,
                scriptUrl: PROD_CHAT_CONFIG.scriptUrl,
                initId: PROD_CHAT_CONFIG.initId,
                typeId: authConfig.typeId,
                authenticId: authConfig.authenticId
            };
        }

        if (!chatConfig) {
            return undefined;
        }

        const { domainUrl, initId, typeId, scriptUrl, authenticId } = chatConfig;

        const deploymentUrl = `${domainUrl}/${authenticId}`;

        const initEmbeddedMessaging = () => {
            if (!window.embeddedservice_bootstrap) {
                return;
            }

            try {
                window.embeddedservice_bootstrap.settings.language = 'en_US';
                window.embeddedservice_bootstrap.init(
                    initId,
                    typeId,
                    deploymentUrl,
                    {
                        scrt2URL: scriptUrl
                    }
                );
            } catch (err) {
                console.error('Error loading Embedded Messaging: ', err);
            }
        };

        const postToEmbeddedMessagingFrame = (message) => {
            const iframe = document.getElementById("embeddedMessagingFrame");
            if (!iframe || !iframe.contentWindow) {
                return;
            }

            iframe.contentWindow.postMessage(message, domainUrl);
        };

        const postIframeDisable = () => postToEmbeddedMessagingFrame('MIAWDisable');
        const postIframeEnable = () => postToEmbeddedMessagingFrame('MIAWEnable');

        const onEmbeddedMessagingReady = () => {
            if (!window.embeddedservice_bootstrap) {
                return;
            }
            applyAuthenticatedChatContext();
        };

        const onWindowMessage = (event) => {
            if (event?.origin !== domainUrl || event?.data?.method !== "EMBEDDED_MESSAGING_DISPATCH_EVENT_TO_HOST") {
                return;
            }

            const entryPayload = event?.data?.data?.eventDetails?.conversationEntry?.entryPayload;
            if (!entryPayload) {
                return;
            }

            let payload;
            try {
                payload = JSON.parse(entryPayload);
            } catch (error) {
                return;
            }

            if (payload?.messageReason === 'Consent') {
                postIframeDisable();
            }

            if (payload?.abstractMessage?.choices) {
                postIframeDisable();
            } else if (payload?.abstractMessage?.choicesResponse) {
                postIframeEnable();
            }
        };

        const script = document.createElement('script');
        script.id = EMBEDDED_MESSAGING_SCRIPT_ID;
        script.type = 'text/javascript';
        script.src = `${deploymentUrl}/assets/js/bootstrap.min.js`;
        script.async = true;
        script.onload = initEmbeddedMessaging;
        script.onerror = () => {
            console.error('Error loading Embedded Messaging bootstrap script.');
        };
        document.body.appendChild(script);

        window.addEventListener("onEmbeddedMessagingReady", onEmbeddedMessagingReady);
        if (isAuthenticated) {
            window.addEventListener("message", onWindowMessage);
        }

        return () => {
            window.removeEventListener("onEmbeddedMessagingReady", onEmbeddedMessagingReady);
            if (isAuthenticated) {
                window.removeEventListener("message", onWindowMessage);
            }
        };

        // Re-injecting bootstrap on every token refresh can leave Salesforce script in an inconsistent state.
    }, [identityToken, isAuthenticated]);

    useEffect(() => {
        applyAuthenticatedChatContext();
    }, [identityToken]);

    return (<></>);
}

export default LiveChatButton;